import { Skeleton } from "@/components/shared/Skeleton";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import { InsightsCard } from "@/components/teacher/report/InsightsCard";
import { MonthlyActivityCard } from "@/components/teacher/report/MonthlyActivityCard";
import { PerformanceCard } from "@/components/teacher/report/PerformanceCard";
import { SkillsBreakdownCard } from "@/components/teacher/report/SkillsBreakdownCard";
import { StreakCard } from "@/components/teacher/report/StreakCard";
import { WeeklySlidesCard } from "@/components/teacher/report/WeeklySlidesCard";
import {
  ReportMonthlyActivityDocument,
  ReportSkillsDocument,
  ReportStreakDocument,
  ReportWeeklySlidesDocument,
} from "@/generated/graphql";
import { useT } from "@/i18n";
import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo, useState } from "react";
import { StudentBreadcrumb } from "./StudentBreadcrumb";
import type { ClassroomScope } from "./classroomScope";
import { useStudentContext } from "./useStudentContext";

/** The student's own report, as their teacher sees it — the same figures the student app shows. */
export function StudentReportPage({
  classroomId,
  studentId,
  scope = "teacher",
}: { classroomId: string; studentId: string; scope?: ClassroomScope }) {
  const t = useT();
  const { classroomName, student } = useStudentContext(scope, classroomId, studentId);

  // The teacher's timezone, resolved once and sent with every query — nothing is persisted.
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const today = useMemo(() => new Date(), []);
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  const [viewDate, setViewDate] = useState({ year: currentYear, month: currentMonth });
  // 0 = the current six-week period; higher is further back. Never negative — there is no
  // future period to page into.
  const [weeklySlidesPeriodOffset, setWeeklySlidesPeriodOffset] = useState(0);

  const canGoToNextMonth =
    viewDate.year < currentYear || (viewDate.year === currentYear && viewDate.month < currentMonth);

  const goToPrevMonth = useCallback(() => {
    setViewDate((prev) =>
      prev.month === 1
        ? { year: prev.year - 1, month: 12 }
        : { year: prev.year, month: prev.month - 1 }
    );
  }, []);
  const goToNextMonth = useCallback(() => {
    if (!canGoToNextMonth) return;
    setViewDate((prev) =>
      prev.month === 12
        ? { year: prev.year + 1, month: 1 }
        : { year: prev.year, month: prev.month + 1 }
    );
  }, [canGoToNextMonth]);

  const skillsQuery = useQuery(ReportSkillsDocument, { variables: { studentId } });
  const streakQuery = useQuery(ReportStreakDocument, { variables: { studentId, timezone } });
  const monthlyActivityQuery = useQuery(ReportMonthlyActivityDocument, {
    variables: { studentId, year: viewDate.year, month: viewDate.month, timezone },
  });
  const weeklySlidesQuery = useQuery(ReportWeeklySlidesDocument, {
    variables: { studentId, timezone, periodOffset: weeklySlidesPeriodOffset },
  });

  const hasAnyData =
    skillsQuery.data || streakQuery.data || monthlyActivityQuery.data || weeklySlidesQuery.data;
  const hasAnyError =
    skillsQuery.error || streakQuery.error || monthlyActivityQuery.error || weeklySlidesQuery.error;

  const reportSkills = skillsQuery.data?.reportSkills;
  const streak = streakQuery.data?.streakAnalysis;
  const monthlyActivity = monthlyActivityQuery.data?.monthlyActivity;
  const weeklySlides = weeklySlidesQuery.data?.weeklySlidesSummary;

  const subtitle = [student?.username, student?.englishName].filter(Boolean).join(" · ");

  return (
    <TeacherPage
      title={student?.name ?? t["student.fallbackName"]}
      subtitle={subtitle || undefined}
      embedded={scope === "admin"}
      breadcrumb={
        <StudentBreadcrumb
          scope={scope}
          classroomId={classroomId}
          classroomName={classroomName}
          studentId={studentId}
          studentName={student?.name ?? null}
          trail={[t["student.report"]]}
        />
      }
    >
      {/* A failed refresh keeps whatever is already on screen; only a cold failure takes over. */}
      {hasAnyError && !hasAnyData ? (
        <p className="app-card body p-6 text-app-overdue">{t["student.reportLoadError"]}</p>
      ) : !hasAnyData ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <Skeleton className="h-48 rounded-card" />
          <Skeleton className="h-48 rounded-card" />
          <Skeleton className="h-64 rounded-card" />
          <Skeleton className="h-64 rounded-card" />
        </div>
      ) : (
        <div className="grid items-start gap-5 lg:grid-cols-2">
          <PerformanceCard recentPerformance={reportSkills?.recentPerformance} />

          <InsightsCard
            strongestSkill={reportSkills?.strongestSkill}
            nextFocus={reportSkills?.nextFocus}
          />

          <SkillsBreakdownCard skills={reportSkills?.skills ?? []} />

          {weeklySlides && (
            <WeeklySlidesCard
              totalSlides={weeklySlides.totalSlides}
              weeks={weeklySlides.weeks}
              onPreviousPeriod={() => setWeeklySlidesPeriodOffset((offset) => offset + 1)}
              onNextPeriod={() => setWeeklySlidesPeriodOffset((offset) => Math.max(offset - 1, 0))}
              canGoToNextPeriod={weeklySlidesPeriodOffset > 0}
            />
          )}

          <StreakCard
            currentStreak={streak?.currentStreak ?? 0}
            longestStreak={streak?.longestStreak ?? 0}
            longestCompletedStreak={streak?.longestCompletedStreak ?? 0}
          />

          <MonthlyActivityCard
            studentId={studentId}
            timezone={timezone}
            year={viewDate.year}
            month={viewDate.month}
            currentYear={currentYear}
            currentMonth={currentMonth}
            totalPracticeTimeMs={monthlyActivity?.totalPracticeTimeMs ?? 0}
            practiceTimeComparison={monthlyActivity?.practiceTimeComparison}
            days={monthlyActivity?.days ?? []}
            onPrevMonth={goToPrevMonth}
            onNextMonth={goToNextMonth}
            onSelectMonth={(year, month) => setViewDate({ year, month })}
            canGoToNextMonth={canGoToNextMonth}
          />
        </div>
      )}
    </TeacherPage>
  );
}
